/**
 * AlertNotificationBridgeService - In-memory state bridge for alert notifications across UI components
 *
 * Boundary: UI-only coordination; does NOT persist to storage or emit to external systems.
 * External dependencies: None (pure in-memory reactive state)
 */
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AlertNotificationData {
  uid: string;
  type: string;
  severity: string;
  title: string;
  message: string;
  timestamp: Date;
  channels: string[];
  metadata?: any;
}

/**
 * Bridges alert notification state between notification center and header badge.
 * Maintains in-memory list of up to 20 recent alerts using a BehaviorSubject.
 *
 * Dependencies:
 * - None (stateless singleton)
 *
 * Side effects:
 * - BehaviorSubject: alertNotifications$ (in-memory only)
 * - No storage, routing, or API calls
 */
@Injectable({
  providedIn: 'root',
})
export class AlertNotificationBridgeService {
  private platformId = inject(PLATFORM_ID);
  private alertNotifications$ = new BehaviorSubject<AlertNotificationData[]>([]);

  constructor() {}

  /**
   * Exposes observable stream of alert notifications for UI subscription
   *
   * @returns Observable emitting arrays of AlertNotificationData
   * - Observable semantics: Emits immediately with current state, then on every mutation; never completes
   * - HTTP errors: N/A (in-memory only)
   * - State touched: None (read-only view of alertNotifications$)
   * - Idempotency: Safe to call multiple times; subscribers share the same Subject
   */
  getAlertNotifications(): Observable<AlertNotificationData[]> {
    return this.alertNotifications$.asObservable();
  }

  /**
   * Returns current alert list snapshot synchronously
   *
   * @returns Array of AlertNotificationData (shallow copy of internal state)
   * - Idempotency: Safe to call repeatedly; no side effects
   */
  getCurrentAlerts(): AlertNotificationData[] {
    return this.alertNotifications$.value;
  }

  /**
   * Adds a new alert to the in-memory list, auto-generating uid and timestamp
   *
   * @param alert - Partial alert data (excluding uid and timestamp)
   * - uid: Auto-generated using crypto.getRandomValues (12-char hex, 48 bits entropy)
   * - timestamp: Set to new Date() at call time
   *
   * @returns void
   * - Observable semantics: Triggers next() on alertNotifications$ immediately
   * - HTTP errors: N/A
   * - State touched: alertNotifications$ (prepends new alert, truncates to 20 items)
   * - Idempotency: NOT idempotent; each call creates a new alert with unique uid
   */
  addAlert(alert: Omit<AlertNotificationData, 'uid' | 'timestamp'>): void {
    // Generate cryptographically secure random ID (6 bytes = 12 hex chars)
    const randomBytes = new Uint8Array(6);
    if (isPlatformBrowser(this.platformId)) {
      window.crypto.getRandomValues(randomBytes);
    } else {
      for (let i = 0; i < randomBytes.length; i++) {
        randomBytes[i] = Math.floor(Math.random() * 256);
      }
    }
    const securePart = Array.from(randomBytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    const newAlert: AlertNotificationData = {
      ...alert,
      uid: `alert-${Date.now()}-${securePart}`,
      timestamp: new Date(),
    };

    const currentAlerts = this.alertNotifications$.value;
    const updatedAlerts = [newAlert, ...currentAlerts];

    // Keep only last 20 alerts
    if (updatedAlerts.length > 20) {
      updatedAlerts.splice(20);
    }

    this.alertNotifications$.next(updatedAlerts);
  }

  /**
   * Removes a single alert by uid
   *
   * @param uid - Unique identifier of the alert to remove
   *
   * @returns void
   * - Observable semantics: Triggers next() on alertNotifications$ if uid matched
   * - HTTP errors: N/A
   * - State touched: alertNotifications$ (mutates array)
   * - Idempotency: Safe to call multiple times with same uid (no-op after first removal)
   */
  removeAlert(uid: string): void {
    const currentAlerts = this.alertNotifications$.value;
    const updatedAlerts = currentAlerts.filter((alert) => alert.uid !== uid);
    this.alertNotifications$.next(updatedAlerts);
  }

  /**
   * Clears all alerts from memory
   *
   * @returns void
   * - Observable semantics: Triggers next([]) on alertNotifications$ immediately
   * - HTTP errors: N/A
   * - State touched: alertNotifications$ (reset to empty array)
   * - Idempotency: Safe to call multiple times; always results in empty state
   */
  clearAllAlerts(): void {
    this.alertNotifications$.next([]);
  }

  /**
   * Returns count of current alerts
   *
   * @returns Number of alerts in memory (0-20)
   * - Idempotency: Safe to call repeatedly; no side effects
   */
  getAlertCount(): number {
    return this.alertNotifications$.value.length;
  }
}
